import "./FileInput.scss";

import { ChangeEvent, ComponentProps, FC, MouseEvent, useRef, useState } from "react";

import FormError from "components/FormError/FormError";
import Icon from "components/Icon/Icon";
import Text from "components/Text/Text";
import { useTranslation } from "modules";

import { forEach } from "lodash-es";

import FileList from "./FileList";

const FileInput: FC<IFileInputProps> = props => {
  const {
    accept = "image/*, .pdf",
    id,
    dataTestId,
    errorMessages = [],
    fileLimit = 20,
    label,
    multiple,
    onChange,
    onClick = () => {},
    onFileChangeError = () => {},
    sizeLimit = 20, // in MB
  } = props;

  // HOOKS
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  // LOCAL STATE
  const [files, setFiles] = useState<HTMLInputElement["files"]>(null);

  // LOCAL METHODS
  const handleOnChange = (event: TFileInputEvents) => {
    const changeEvent = {
      ...event,
      target: { ...event.target, id, value: inputRef.current?.value },
      files: inputRef.current?.files,
    } as FileInputChangeEvent;

    onChange(changeEvent);
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const uniqueFiles = getFiles(files);
    forEach(event.target.files, file => {
      const isDuplicate = uniqueFiles.some(
        selectedFile =>
          selectedFile.name === file.name &&
          selectedFile.size === file.size &&
          selectedFile.type === file.type
      );
      const isFileLimitExceeded = uniqueFiles.length >= fileLimit;
      const isFileSizeExceeded = file.size > sizeLimit * (1024 * 1024);

      if (isFileLimitExceeded) {
        onFileChangeError("file-limit-error", file);
        return false; // Exit iteration it further processing if file limit reached
      }

      if (isFileSizeExceeded) {
        onFileChangeError("file-size-error", file);
        return;
      }

      if (!isDuplicate) {
        uniqueFiles.push(file);
      }
    });
    setFileList(uniqueFiles);
    handleOnChange(event);
  };

  const handleFileRemove = (event: MouseEvent, fileName: File["name"]) => {
    onClick(event);
    const filteredFiles = getFiles(files).filter(file => file.name !== fileName);
    setFileList(filteredFiles);
    handleOnChange(event);
  };

  const getFiles = (fileList?: HTMLInputElement["files"]) => {
    return Array.from(fileList || []);
  };

  const setFileList = (files: FileList | File[]) => {
    const dataTransfer = new DataTransfer();
    forEach(files, file => {
      dataTransfer.items.add(file);
    });
    if (inputRef.current) {
      inputRef.current.files = dataTransfer.files;
      setFiles(dataTransfer.files);
    }
  };

  // RENDER
  return (
    <div className="file-input" data-testid={dataTestId}>
      {files && <FileList files={files} onRemove={handleFileRemove} />}

      <input
        accept={accept}
        className="file-input__input"
        id={id}
        multiple={multiple}
        onChange={handleFileSelect}
        onClick={onClick}
        ref={inputRef}
        type="file"
      />
      <label className="file-input__label" htmlFor={id}>
        <Icon src="DocumentUpload" />
        <Text tag="l5" text={label || t("input.file.placeholder", "Choose File")} />
      </label>
      <FormError className="file-input__errors" error={errorMessages} />
    </div>
  );
};

export default FileInput;

/* ------------------    TYPES    ------------------ */
interface IFileInputProps extends Partial<HTMLInputElement> {
  id: string;
  accept?: string;
  dataTestId?: string;
  errorMessages?: string[];
  fileLimit?: number;
  label?: string;
  multiple?: boolean;
  sizeLimit?: number; // in MB
  onChange(event: FileInputChangeEvent): void;
  onClick?(event: MouseEvent): void;
  onFileChangeError?(errorName: "file-limit-error" | "file-size-error", file: File): void;
}

type FileInputChangeEvent = TFileInputEvents & {
  files: HTMLInputElement["files"];
};

type TFileInputEvents = MouseEvent | ChangeEvent<HTMLInputElement>;

export type IFileInputComponentProps = ComponentProps<typeof FileInput>;
